import { useCallback, useMemo } from 'react';

import {
  DEFAULT_LOCALE,
  getStrings,
  interpolate,
  resolveLocale,
  type Locale,
  type Strings,
} from '@llura/constants';

import { useI18nStore } from '../stores/i18n.store';

interface UseStringsResult {
  locale: Locale;
  strings: Strings;
  setLocale: (locale: Locale) => void;
  /** Replace `{key}` placeholders in a template string. */
  interp: (template: string, vars: Record<string, string | number>) => string;
}

function resolveBrowserLocale(): Locale {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE;
  return resolveLocale(navigator.language);
}

export function useStrings(): UseStringsResult {
  const stored = useI18nStore((s) => s.locale);
  const setLocale = useI18nStore((s) => s.setLocale);

  const locale = stored ?? resolveBrowserLocale();
  const strings = useMemo(() => getStrings(locale), [locale]);
  const interp = useCallback(
    (template: string, vars: Record<string, string | number>) => interpolate(template, vars),
    [],
  );

  return { locale, strings, setLocale, interp };
}
